import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { LanguageOptions } from '../../entities/LanguageOptions/index.js';
import { Availability } from '../../entities/Availability/index.js';
import { toast } from 'react-toastify';
import { hasChangesAvailability } from '../../shared/utils/compareAvailability.js';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { Form, Row, Col } from 'react-bootstrap';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const EditEventPage = () => {
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [oldData, setOldData] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [form, setForm] = useState();
  const [saving, setSaving] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    apiRequests.events.getCategories().then((res) => {
      setCategories(res.items);
    });
    apiRequests.events.getFormats().then((res) => {
      setFormats(res.items);
    });
    apiRequests.events.getTimezones().then((res) => {
      setTimezones(res.items);
    });
    apiRequests.events.getById(id).then((res) => {
      // Если SEOMetatags не существует в ответе API, добавляем его с пустыми значениями
      if (!res.SEOMetatags) {
        res.SEOMetatags = {
          Title: null,
          Description: null,
          Keywords: null,
        };
      }
      setOldData(res);
      setForm(res);
    });
  }, []);

  const handleSubmit = async () => {
    const body = new FormData();

    if (mainImage) {
      body.append('mainImg', mainImage);
    }

    if (hasChangesAvailability(oldData.availability, form.availability)) {
      const query = await apiRequests.events.updateAvailability(form.availability);
    }
    body.append('model', JSON.stringify(form));
    const query = apiRequests.events.update(body);
    toast.promise(query, {
      pending: 'Запрос к серверу',
      error: 'Произошла ошибка',
      success: 'Мероприятие успешно изменено',
    });
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/events')}
      saving={saving}
      submitText="Сохранить"
      savingText="Сохранение..."
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Введите название"
              />
            </FormField>

            <FormField label="Организатор">
              <Form.Control
                type="text"
                value={form.eventOrganizer}
                onChange={(e) => setForm((prev) => ({ ...prev, eventOrganizer: e.target.value }))}
                placeholder="Введите организатора"
              />
            </FormField>

            <FormField label="Описание">
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Введите описание"
              />
            </FormField>

            <Row>
              <Col md={4}>
                <FormField label="Категория">
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, categoryId: Number(e.target.value) }))}
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Формат">
                  <Form.Select
                    value={form.formatId}
                    onChange={(e) => setForm((prev) => ({ ...prev, formatId: Number(e.target.value) }))}
                  >
                    {formats.map((f) => (
                      <option key={f.id} value={f.id}>{f.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Часовой пояс">
                  <Form.Select
                    value={form.timeZoneId}
                    onChange={(e) => setForm((prev) => ({ ...prev, timeZoneId: Number(e.target.value) }))}
                  >
                    {timezones.map((tz) => (
                      <option key={tz.id} value={tz.id}>{tz.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
            </Row>
          </Col>

          <Col lg={4}>
            <FormField label="Изображение">
              <UploadPicture onSelect={setMainImage} initialValue={form.imgPath} entityType="event" />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Настройки">
        <Row>
          <Col lg={6}>
            <LanguageOptions
              initialValue={form.mainLanguageId}
              onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
            />
          </Col>
        </Row>

        <div className="mt-4">
          <Form.Label className="fw-medium mb-3">Доступность по странам</Form.Label>
          <Availability
            initialValues={form.availability}
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, availableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, allowedCountriesIds: val },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, disallowedCountriesIds: val },
              }))
            }
          />
        </div>
      </FormSection>

      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={form.SEOMetatags}
          onChange={(metaTags) => setForm((prev) => ({ ...prev, SEOMetatags: metaTags }))}
        />
      </FormSection>
    </FormLayout>
  );
};

export default EditEventPage;
